/**
 * /admin/orders — جدول طلبات موحَّد للأدمن.
 *
 * يجمع gift_notifications + ambassador_events + gift_redemptions في عرض واحد
 * مع فلاتر (نوع، حالة WhatsApp، تاريخ، بحث) و pagination.
 */
import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "@/lib/toast";
import {
  Loader2,
  Search,
  ExternalLink,
  Check,
  Clock,
  X as XIcon,
  Gift,
  User as UserIcon,
  RefreshCw,
  DownloadCloud,
  Sparkles,
  Coins,
  Ticket,
  ChevronRight,
  Truck,
  UploadCloud,
} from "lucide-react";
import { optStr, optEnum } from "@/lib/search-validators";
import { useAdminPageAuth } from "@/hooks/useAdminPageAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useLocale } from "@/contexts/LocaleContext";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { listOrdersAdmin } from "@/utils/admin.functions";
import { resyncShopifyOrder } from "@/lib/admin/resyncOrder.functions";
import { importShopifyOrders } from "@/lib/admin/importOrders.functions";
import { getOrderTransactions, type OrderTxn } from "@/lib/admin/orderTransactions.functions";
import { AdminOrderDetail } from "@/components/admin/AdminOrderDetail";

import {
  getOrdersDeliveryStatus,
  listDrivers,
  assignOrderToDriver,
  pushOrderToShopify,
} from "@/lib/admin/drivers.functions";
import { cn } from "@/lib/utils";

const searchSchema = (search: Record<string, unknown>): {
  whatsapp?: "all" | "sent" | "pending" | "failed";
  type?: "all" | "gift" | "self";
  q?: string;
} => ({
  whatsapp: optEnum(search.whatsapp, ["all", "sent", "pending", "failed"] as const),
  type: optEnum(search.type, ["all", "gift", "self"] as const),
  q: optStr(search.q),
});

export const Route = createFileRoute("/admin/orders")({
  component: AdminOrdersPage,
  validateSearch: searchSchema,
  head: () => ({ meta: [{ title: "Admin · Orders — Lecmo" }] }),
});

const PAGE_SIZE = 50;

// رابط Shopify Admin للطلب — order_id من Storefront API يأتي بصيغة gid://shopify/Order/123
function shopifyOrderUrl(orderId: string): string | null {
  const match = orderId.match(/Order\/(\d+)/);
  if (!match) return null;
  return `https://admin.shopify.com/store/lecmo/orders/${match[1]}`;
}

function AdminOrdersPage() {
  const { user, loading: authLoading } = useAdminPageAuth("/admin/orders");
  const { isAdmin, loading: roleLoading } = useIsAdmin();
  const { dir, locale } = useLocale();
  const search = Route.useSearch();
  const isAr = locale === "ar";
  const fetchOrders = useServerFn(listOrdersAdmin);
  const resync = useServerFn(resyncShopifyOrder);
  const importOrders = useServerFn(importShopifyOrders);
  const pushToShopify = useServerFn(pushOrderToShopify);
  const queryClient = useQueryClient();
  const [resyncingId, setResyncingId] = React.useState<string | null>(null);
  const [importing, setImporting] = React.useState(false);
  const [pushingId, setPushingId] = React.useState<string | null>(null);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  const handleImport = React.useCallback(async () => {
    const msg = isAr
      ? "استيراد آخر ١٠٠ طلب من Shopify؟ يُستخدم عند عدم تسجيل webhooks بعد (قبل claim المتجر). آمن للتكرار (idempotent)."
      : "Import the last 100 orders from Shopify? Used when webhooks aren't registered yet (pre-claim). Safe to repeat (idempotent).";
    if (typeof window !== "undefined" && !window.confirm(msg)) return;
    setImporting(true);
    try {
      const r = await importOrders({ data: { limit: 100 } });
      if (!r.ok) {
        toast.error(isAr ? "فشل الاستيراد" : "Import failed", { description: r.message });
        return;
      }
      toast.success(
        isAr
          ? `تمّ الاستيراد: ${r.imported}/${r.total}`
          : `Imported: ${r.imported}/${r.total}`,
        {
          description: r.errors.length
            ? (isAr ? "أخطاء جزئيّة: " : "Partial errors: ") + r.errors.join(" · ")
              : isAr
                ? "كلّ الطلبات الموجودة الآن في الجدول."
                : "All orders are now in the table.",
        },
      );
      await queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
    } catch (e) {
      toast.error(isAr ? "خطأ" : "Error", { description: (e as Error).message });
    } finally {
      setImporting(false);
    }
  }, [importOrders, isAr, queryClient]);

  const handleResync = React.useCallback(
    async (orderId: string, orderName: string | null) => {
      const confirmMsg = isAr
        ? `إعادة مزامنة الطلب ${orderName ?? orderId} من Shopify؟ سيُعاد فحص حالته (مدفوع/مُسلَّم/ملغى/مُسترد) وتصحيح رصيد السفير إن لزم.`
        : `Resync order ${orderName ?? orderId} from Shopify? This will re-check its status (paid/fulfilled/cancelled/refunded) and correct ambassador credit if needed.`;
      if (typeof window !== "undefined" && !window.confirm(confirmMsg)) return;
      setResyncingId(orderId);
      try {
        const r = await resync({ data: { orderId } });
        if (!r.ok) {
          toast.error(isAr ? "تعذّرت المزامنة" : "Resync failed", {
            description: r.message,
          });
          return;
        }
        if (!r.changed) {
          toast.success(
            isAr ? "متطابق بالفعل — لا تغيير" : "Already in sync — no change",
            {
              description: `${r.financialStatus ?? "?"} · ${r.fulfillmentStatus ?? "?"}`,
            },
          );
        } else {
          toast.success(isAr ? "تمّت المزامنة" : "Resynced successfully", {
            description: r.actions.join(" · "),
          });
        }
      } catch (e) {
        toast.error(isAr ? "خطأ" : "Error", {
          description: (e as Error).message,
        });
      } finally {
        setResyncingId(null);
      }
    },
    [resync, isAr],
  );

  const handlePushToShopify = React.useCallback(
    async (orderId: string, orderName: string | null) => {
      const msg = isAr
        ? `دفع حالة التسليم المحليّ للطلب ${orderName ?? orderId} إلى Shopify؟ سيتمّ تعليمه Fulfilled وتسجيل الدفع إن حصّله السائق.`
        : `Push local delivery of ${orderName ?? orderId} to Shopify? It will be marked fulfilled and payment recorded if the driver collected it.`;
      if (typeof window !== "undefined" && !window.confirm(msg)) return;
      setPushingId(orderId);
      try {
        const r = await pushToShopify({ data: { shopify_order_id: orderId } });
        const parts = [
          r.fulfilled ? (isAr ? "Fulfilled ✓" : "Fulfilled ✓") : null,
          r.paid ? (isAr ? "Paid ✓" : "Paid ✓") : null,
          ...r.skipped,
        ].filter(Boolean) as string[];
        if (r.errors.length && !r.fulfilled && !r.paid) {
          toast.error(isAr ? "تعذّر الدفع إلى Shopify" : "Push failed", {
            description: r.errors.join(" · "),
          });
        } else {
          toast.success(isAr ? "تمّ الدفع إلى Shopify" : "Pushed to Shopify", {
            description: parts.join(" · ") || (r.errors.join(" · ") || undefined),
          });
        }
      } catch (e) {
        toast.error(isAr ? "خطأ" : "Error", { description: (e as Error).message });
      } finally {
        setPushingId(null);
      }
    },
    [pushToShopify, isAr],
  );

  const [type, setType] = React.useState<"all" | "gift" | "self">(search.type ?? "all");
  const [whatsapp, setWhatsapp] = React.useState<"all" | "sent" | "pending" | "failed">(
    search.whatsapp ?? "all",
  );
  const [searchText, setSearchText] = React.useState(search.q ?? "");
  const [debouncedSearch, setDebouncedSearch] = React.useState(search.q ?? "");
  const [page, setPage] = React.useState(0);

  // debounce بحث
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  // أعد للصفحة ٠ عند تغيير أيّ فلتر
  React.useEffect(() => {
    setPage(0);
  }, [type, whatsapp, debouncedSearch]);


  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["admin-orders", type, whatsapp, debouncedSearch, page],
    queryFn: () =>
      fetchOrders({
        data: {
          page,
          pageSize: PAGE_SIZE,
          type,
          whatsapp,
          search: debouncedSearch || undefined,
        },
      }),
    enabled: !!user && isAdmin,
    staleTime: 30_000,
  });

  // ── التوصيل المحلّي: حالة كلّ طلب + قائمة السائقين ──
  const fetchDeliveryStatus = useServerFn(getOrdersDeliveryStatus);
  const fetchDrivers = useServerFn(listDrivers);
  const assignDriver = useServerFn(assignOrderToDriver);
  const [assigningId, setAssigningId] = React.useState<string | null>(null);

  const shopifyOrderIds = React.useMemo(
    () => (data?.rows ?? []).map((r) => r.order_id).filter(Boolean) as string[],
    [data],
  );

  const { data: deliveryData } = useQuery({
    queryKey: ["admin-orders-delivery", shopifyOrderIds],
    queryFn: () => fetchDeliveryStatus({ data: { shopify_order_ids: shopifyOrderIds } }),
    enabled: !!user && isAdmin && shopifyOrderIds.length > 0,
    staleTime: 15_000,
  });

  const { data: driversData } = useQuery({
    queryKey: ["admin-orders-drivers"],
    queryFn: () => fetchDrivers(),
    enabled: !!user && isAdmin,
    staleTime: 60_000,
  });

  const activeDrivers = React.useMemo(
    () => (driversData?.drivers ?? []).filter((d) => d.status === "active"),
    [driversData],
  );

  const handleAssign = async (orderUuid: string, driverId: string | null) => {
    setAssigningId(orderUuid);
    try {
      await assignDriver({ data: { order_id: orderUuid, driver_id: driverId } });
      toast.success(
        driverId
          ? isAr
            ? "تمّ إسناد الطلب للسائق."
            : "Order assigned to driver."
          : isAr
            ? "تمّ إلغاء الإسناد."
            : "Assignment cleared.",
      );
      await queryClient.invalidateQueries({ queryKey: ["admin-orders-delivery"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : isAr ? "فشل الإسناد." : "Assignment failed.");
    } finally {
      setAssigningId(null);
    }
  };


  if (authLoading || roleLoading) {
    return (
      <SiteShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </SiteShell>
    );
  }

  if (!user) return null;

  if (!isAdmin) {
    return (
      <SiteShell>
        <div className="mx-auto max-w-2xl px-5 pb-24 pt-8 text-center" dir={dir}>
          <h1 className="t-h1 text-foreground">403</h1>
          <p className="mt-4 text-sm text-muted-foreground">
            {isAr ? "هذه الصفحة للمشرفين فقط." : "Admin access required."}
          </p>
          <Link to="/account" className="mt-6 inline-block text-sm text-primary underline">
            {isAr ? "العودة" : "Back"}
          </Link>
        </div>
      </SiteShell>
    );
  }

  const rows = data?.rows ?? [];
  const total = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6" dir={dir}>
        <header className="mb-2 flex items-start justify-between gap-3">
          <div>
            <h1 className="t-h1 text-foreground">{isAr ? "الطلبات" : "Orders"}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {isAr
                ? "كلّ الطلبات الواصلة من Shopify مع حالة WhatsApp والسفير المحيل."
                : "All Shopify orders with WhatsApp status and referring ambassador."}
            </p>
          </div>
          <button
            type="button"
            onClick={handleImport}
            disabled={importing}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-none border border-border bg-background px-3 py-2 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
            title={
              isAr
                ? "استيراد الطلبات الموجودة في Shopify (قبل تسجيل webhooks)"
                : "Import existing Shopify orders (before webhooks are registered)"
            }
          >
            {importing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <DownloadCloud className="h-3.5 w-3.5" />
            )}
            {isAr ? "استيراد من Shopify" : "Import from Shopify"}
          </button>
        </header>

        <AdminNav />

        {/* فلاتر */}
        <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={
                isAr
                  ? "بحث بالرقم، الإيميل، الهاتف، الاسم…"
                  : "Search by order #, email, phone, name…"
              }
              className="w-full rounded-none border border-border bg-background ps-10 pe-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            />
          </div>
          <SegmentedControl
            value={type}
            onChange={setType}
            options={[
              { value: "all", label: isAr ? "الكل" : "All" },
              { value: "gift", label: isAr ? "هدايا" : "Gifts" },
              { value: "self", label: isAr ? "لنفسي" : "Self" },
            ]}
          />
          <SegmentedControl
            value={whatsapp}
            onChange={setWhatsapp}
            options={[
              { value: "all", label: isAr ? "الكل" : "All" },
              { value: "sent", label: isAr ? "مُرسل" : "Sent" },
              { value: "pending", label: isAr ? "معلّق" : "Pending" },
              { value: "failed", label: isAr ? "فشل" : "Failed" },
            ]}
          />
        </div>

        {/* عدّاد + Pagination علويّ */}
        <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
          <div>
            {isLoading || isFetching ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                {isAr ? "جارٍ التحميل…" : "Loading…"}
              </span>
            ) : (
              <span>
                {isAr
                  ? `${total} طلب · صفحة ${page + 1} من ${totalPages}`
                  : `${total} orders · Page ${page + 1} of ${totalPages}`}
              </span>
            )}
          </div>
          {totalPages > 1 && (
            <div className="flex gap-1">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded-none border border-border bg-background px-3 py-1 text-xs disabled:opacity-40 hover:bg-muted"
              >
                {isAr ? "السابق" : "Prev"}
              </button>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-none border border-border bg-background px-3 py-1 text-xs disabled:opacity-40 hover:bg-muted"
              >
                {isAr ? "التالي" : "Next"}
              </button>
            </div>
          )}
        </div>

        {/* جدول */}
        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-none border border-dashed border-border p-12 text-center">
            <p className="text-sm text-muted-foreground">
              {isAr ? "لا توجد طلبات تطابق هذه الفلاتر." : "No orders match these filters."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-none border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-3 text-start font-medium">
                    {isAr ? "الطلب" : "Order"}
                  </th>
                  <th className="px-3 py-3 text-start font-medium">
                    {isAr ? "الحالة" : "Status"}
                  </th>
                  <th className="px-3 py-3 text-start font-medium">
                    {isAr ? "التاريخ / الوقت" : "Date / Time"}
                  </th>
                  <th className="px-3 py-3 text-start font-medium">
                    {isAr ? "العميل" : "Customer"}
                  </th>
                  <th className="px-3 py-3 text-start font-medium">
                    {isAr ? "النوع" : "Type"}
                  </th>
                  <th className="px-3 py-3 text-start font-medium">
                    {isAr ? "المستلِم" : "Recipient"}
                  </th>
                  <th className="px-3 py-3 text-start font-medium">WhatsApp</th>
                  <th className="px-3 py-3 text-start font-medium">
                    {isAr ? "السفير" : "Ambassador"}
                  </th>
                  <th className="px-3 py-3 text-start font-medium">
                    {isAr ? "المكافآت/الخصم" : "Rewards/Discount"}
                  </th>
                  <th className="px-3 py-3 text-start font-medium">
                    {isAr ? "التوصيل" : "Delivery"}
                  </th>
                  <th className="px-3 py-3 text-start font-medium" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const shopifyUrl = shopifyOrderUrl(r.order_id);
                  return (
                    <React.Fragment key={r.id}>
                    <tr className="border-t border-border hover:bg-muted/20">
                      <td className="px-3 py-3 font-medium text-foreground tabular-nums">
                        {r.order_name ?? "—"}
                      </td>
                      <td className="px-3 py-3">
                        <OrderStatusCell row={r} isAr={isAr} />
                      </td>
                      <td className="px-3 py-3 text-muted-foreground tabular-nums">
                        {r.created_at ? (
                          <div className="flex flex-col gap-0.5">
                            <span>
                              {new Date(r.created_at).toLocaleDateString(locale, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </span>
                            <span className="text-xs text-muted-foreground/80">
                              {new Date(r.created_at).toLocaleTimeString(locale, {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })}
                            </span>
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-foreground">{r.customer_email ?? "—"}</div>
                        {r.customer_phone && (
                          <div className="text-xs text-muted-foreground">{r.customer_phone}</div>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {r.is_gift ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                            <Gift className="h-3 w-3" />
                            {isAr ? "هديّة" : "Gift"}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                            <UserIcon className="h-3 w-3" />
                            {isAr ? "لنفسي" : "Self"}
                          </span>
                        )}
                        {r.has_redemption && (
                          <span className="ms-1 inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-400">
                            {isAr ? "+ عيّنة" : "+ sample"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        {r.recipient_name ? (
                          <>
                            <div className="text-foreground">{r.recipient_name}</div>
                            {r.recipient_phone && (
                              <div className="text-xs text-muted-foreground">
                                {r.recipient_phone}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <WhatsAppBadge status={r.whatsapp_status} error={r.whatsapp_error} />
                      </td>
                      <td className="px-3 py-3">
                        {r.ambassador ? (
                          <div>
                            <div className="font-mono text-xs text-foreground">
                              {r.ambassador.code}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {r.ambassador.display_name}
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <RewardsBadges rewards={r.rewards} isAr={isAr} />
                      </td>
                      <td className="px-3 py-3">
                        <DeliveryCell
                          row={
                            (deliveryData?.rows ?? []).find(
                              (d) => d.shopify_order_id === r.order_id,
                            ) ?? null
                          }
                          drivers={activeDrivers}
                          busy={
                            !!assigningId &&
                            assigningId ===
                              (deliveryData?.rows ?? []).find(
                                (d) => d.shopify_order_id === r.order_id,
                              )?.order_uuid
                          }
                          onAssign={handleAssign}
                          isAr={isAr}
                        />
                      </td>
                      <td className="px-3 py-3 text-end">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedId((id) => (id === r.id ? null : r.id))
                            }
                            className="inline-flex h-7 w-7 items-center justify-center rounded-none border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
                            title={isAr ? "تفاصيل" : "Details"}
                            aria-label={isAr ? "تفاصيل" : "Details"}
                          >
                            <ChevronRight
                              className={cn(
                                "h-3.5 w-3.5 transition-transform",
                                expandedId === r.id && "rotate-90",
                                dir === "rtl" && expandedId !== r.id && "rotate-180",
                              )}
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleResync(r.order_id, r.order_name ?? null)}
                            disabled={resyncingId === r.order_id}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-none border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                            title={
                              isAr
                                ? "إعادة مزامنة من Shopify (لإصلاح webhook فاشل)"
                                : "Resync from Shopify (fix failed webhook)"
                            }
                            aria-label={isAr ? "إعادة مزامنة" : "Resync"}
                          >
                            {resyncingId === r.order_id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handlePushToShopify(r.order_id, r.order_name ?? null)
                            }
                            disabled={pushingId === r.order_id}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-none border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                            title={
                              isAr
                                ? "دفع التسليم إلى Shopify (Fulfilled + تحصيل الدفع)"
                                : "Push delivery to Shopify (fulfill + capture payment)"
                            }
                            aria-label={isAr ? "دفع إلى Shopify" : "Push to Shopify"}
                          >
                            {pushingId === r.order_id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <UploadCloud className="h-3.5 w-3.5" />
                            )}
                          </button>
                          {shopifyUrl && (
                            <a
                              href={shopifyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                              title={isAr ? "فتح في Shopify" : "Open in Shopify"}
                            >
                              Shopify
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedId === r.id && (
                      <tr className="border-t border-border bg-muted/10">
                        <td colSpan={11} className="px-4 py-4 space-y-4">
                          <AdminOrderDetail orderId={r.order_id} isAr={isAr} />
                          <RewardsDetail rewards={r.rewards} isAr={isAr} />
                          <OrderTransactionsList orderId={r.order_id} isAr={isAr} />
                        </td>

                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SiteShell>
  );
}

type Rewards = {
  loyalty_points: number;
  ambassador_cash: { amount: number; currency: string; state: string } | null;
  discount_code: string | null;
  combined_redemption: {
    total_aed: number;
    points_used: number;
    points_value_aed: number;
    cash_used_aed: number;
  } | null;
};

type OrderStatusRow = {
  financial_status: string | null;
  fulfillment_status: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
};

const FINANCIAL_LABELS: Record<string, { ar: string; en: string; tone: string }> = {
  paid: { ar: "مدفوع", en: "Paid", tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
  pending: { ar: "معلّق", en: "Pending", tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400" },
  refunded: { ar: "مُسترَد", en: "Refunded", tone: "bg-primary/10 text-primary" },
  partially_refunded: { ar: "استرداد جزئي", en: "Partially refunded", tone: "bg-primary/10 text-primary" },
  voided: { ar: "ملغى مالياً", en: "Voided", tone: "bg-destructive/10 text-destructive" },
};

const FULFILMENT_LABELS: Record<string, { ar: string; en: string; tone: string }> = {
  fulfilled: { ar: "مُجهَّز", en: "Fulfilled", tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
  partial: { ar: "تجهيز جزئي", en: "Partial", tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400" },
  restocked: { ar: "مُعاد تخزينه", en: "Restocked", tone: "bg-primary/10 text-primary" },
};

function OrderStatusCell({ row, isAr }: { row: OrderStatusRow; isAr: boolean }) {
  if (row.cancelled_at) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] text-destructive">
        {isAr ? "ملغى" : "Cancelled"}
      </span>
    );
  }

  const financial = FINANCIAL_LABELS[row.financial_status ?? ""] ?? {
    ar: row.financial_status ?? (isAr ? "—" : "—"),
    en: row.financial_status ?? "—",
    tone: "bg-muted text-muted-foreground",
  };
  const fulfilment = FULFILMENT_LABELS[row.fulfillment_status ?? ""] ?? null;

  return (
    <div className="flex flex-wrap gap-1">
      <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[11px]", financial.tone)}>
        {isAr ? financial.ar : financial.en}
      </span>
      {fulfilment && (
        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[11px]", fulfilment.tone)}>
          {isAr ? fulfilment.ar : fulfilment.en}
        </span>
      )}
    </div>
  );
}

const DELIVERY_LABELS: Record<string, { ar: string; en: string; tone: string }> = {
  pending: { ar: "لم يُسنَد", en: "Unassigned", tone: "bg-muted text-muted-foreground" },
  assigned: { ar: "مُسنَد", en: "Assigned", tone: "bg-primary/10 text-primary" },
  picked_up: { ar: "تمّ الاستلام", en: "Picked up", tone: "bg-primary/10 text-primary" },
  out_for_delivery: {
    ar: "في الطريق",
    en: "Out for delivery",
    tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  },
  delivered: {
    ar: "تمّ التوصيل",
    en: "Delivered",
    tone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  },
  failed: { ar: "فشل", en: "Failed", tone: "bg-destructive/10 text-destructive" },
  returned: { ar: "مُرتجَع", en: "Returned", tone: "bg-destructive/10 text-destructive" },
};

function DeliveryCell({
  row,
  drivers,
  busy,
  onAssign,
  isAr,
}: {
  row: {
    order_uuid: string;
    delivery_status: string;
    assigned_driver_id: string | null;
    driver_name: string | null;
  } | null;
  drivers: { id: string; display_name: string }[];
  busy: boolean;
  onAssign: (orderUuid: string, driverId: string | null) => void;
  isAr: boolean;
}) {
  if (!row) {
    return (
      <span className="text-xs text-muted-foreground">
        {isAr ? "لا سجلّ محلّي" : "No local record"}
      </span>
    );
  }

  const meta = DELIVERY_LABELS[row.delivery_status] ?? DELIVERY_LABELS["pending"]!;
  const locked = row.delivery_status !== "pending" && row.delivery_status !== "assigned";

  return (
    <div className="space-y-1.5">
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px]",
          meta.tone,
        )}
      >
        <Truck className="h-3 w-3" />
        {isAr ? meta.ar : meta.en}
      </span>
      {row.driver_name && (
        <div className="text-[11px] text-muted-foreground">{row.driver_name}</div>
      )}
      {!locked && (
        <select
          value={row.assigned_driver_id ?? ""}
          disabled={busy}
          onChange={(e) => onAssign(row.order_uuid, e.target.value || null)}
          className="w-full max-w-[150px] rounded-none border border-border bg-background px-2 py-1 text-xs text-foreground focus:border-primary focus:outline-none disabled:opacity-50"
          aria-label={isAr ? "إسناد سائق" : "Assign driver"}
        >
          <option value="">{isAr ? "— بلا سائق —" : "— No driver —"}</option>
          {drivers.map((d) => (
            <option key={d.id} value={d.id}>
              {d.display_name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function RewardsBadges({ rewards, isAr }: { rewards: Rewards; isAr: boolean }) {
  const hasAny =
    rewards.loyalty_points > 0 || rewards.ambassador_cash || rewards.discount_code;
  if (!hasAny) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {rewards.loyalty_points > 0 && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-700 dark:text-amber-400"
          title={isAr ? "نقاط ولاء للعميل" : "Loyalty points to customer"}
        >
          <Sparkles className="h-3 w-3" />+{rewards.loyalty_points} pt
        </span>
      )}
      {rewards.ambassador_cash && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-400"
          title={isAr ? "كاش للسفير" : "Cash to ambassador"}
        >
          <Coins className="h-3 w-3" />+{rewards.ambassador_cash.amount.toFixed(2)}{" "}
          {rewards.ambassador_cash.currency}
        </span>
      )}
      {rewards.discount_code && (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary"
          title={rewards.discount_code}
        >
          <Ticket className="h-3 w-3" />
          {rewards.combined_redemption
            ? `−${rewards.combined_redemption.total_aed.toFixed(2)} AED`
            : isAr
              ? "كوبون"
              : "Coupon"}
        </span>
      )}
    </div>
  );
}

function RewardsDetail({ rewards, isAr }: { rewards: Rewards; isAr: boolean }) {
  return (
    <div className="grid grid-cols-1 gap-4 text-xs sm:grid-cols-2 lg:grid-cols-4">
      <DetailBlock label={isAr ? "نقاط ولاء للعميل" : "Loyalty points to customer"}>
        {rewards.loyalty_points > 0 ? (
          <span className="text-amber-700 dark:text-amber-400">
            +{rewards.loyalty_points} {isAr ? "نقطة" : "points"}
          </span>
        ) : (
          <span className="text-muted-foreground">{isAr ? "لا شيء" : "None"}</span>
        )}
      </DetailBlock>
      <DetailBlock label={isAr ? "كاش للسفير المُحيل" : "Cash to referring ambassador"}>
        {rewards.ambassador_cash ? (
          <span className="text-emerald-700 dark:text-emerald-400">
            +{rewards.ambassador_cash.amount.toFixed(2)} {rewards.ambassador_cash.currency}{" "}
            <span className="text-muted-foreground">({rewards.ambassador_cash.state})</span>
          </span>
        ) : (
          <span className="text-muted-foreground">{isAr ? "لا شيء" : "None"}</span>
        )}
      </DetailBlock>
      <DetailBlock label={isAr ? "الكوبون المستخدم" : "Coupon used"}>
        {rewards.discount_code ? (
          <span className="font-mono text-foreground">{rewards.discount_code}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </DetailBlock>
      <DetailBlock label={isAr ? "تفصيل الخصم" : "Discount breakdown"}>
        {rewards.combined_redemption ? (
          <div className="space-y-0.5">
            <div className="text-foreground">
              −{rewards.combined_redemption.total_aed.toFixed(2)} AED
            </div>
            <div className="text-muted-foreground">
              {rewards.combined_redemption.points_used} pt (
              {rewards.combined_redemption.points_value_aed.toFixed(2)} AED) +{" "}
              {rewards.combined_redemption.cash_used_aed.toFixed(2)} AED{" "}
              {isAr ? "كاش" : "cash"}
            </div>
          </div>
        ) : rewards.discount_code ? (
          <span className="text-muted-foreground">
            {isAr
              ? "كوبون عاديّ (ليس من المحفظة الموحَّدة)"
              : "Plain coupon (not unified wallet)"}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </DetailBlock>
    </div>
  );
}

function DetailBlock({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

function WhatsAppBadge({
  status,
  error,
}: {
  status: "sent" | "delivered" | "pending" | "failed" | "none";
  error: string | null;
}) {
  const { locale } = useLocale();
  const isAr = locale === "ar";

  if (status === "none") {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (status === "delivered" || status === "sent") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-700 dark:text-emerald-400">
        <Check className="h-3 w-3" />
        {status === "delivered"
          ? isAr
            ? "تمّ التسليم"
            : "Delivered"
          : isAr
            ? "مُرسل"
            : "Sent"}
      </span>
    );
  }
  if (status === "pending") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700 dark:text-amber-400">
        <Clock className="h-3 w-3" />
        {isAr ? "معلّق" : "Pending"}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-0.5 text-xs text-rose-700 dark:text-rose-400"
      title={error ?? undefined}
    >
      <XIcon className="h-3 w-3" />
      {isAr ? "فشل" : "Failed"}
    </span>
  );
}

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div className="inline-flex rounded-none border border-border bg-background text-xs">
      {options.map((opt, i) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={cn(
            "px-3 py-2 transition-colors",
            i > 0 && "border-s border-border",
            value === opt.value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── الحركات الماليّة المرتبطة بالطلب ───
const TXN_TYPE_LABELS: Record<string, { ar: string; en: string; tone: string }> = {
  purchase_credit: { ar: "عمولة شراء", en: "Purchase commission", tone: "emerald" },
  refund_reversal: { ar: "خصم استرجاع", en: "Refund reversal", tone: "rose" },
  click_credit: { ar: "مكافأة نقرة", en: "Click credit", tone: "sky" },
  milestone_reward: { ar: "مكافأة Milestone", en: "Milestone reward", tone: "amber" },
  payout: { ar: "سحب", en: "Payout", tone: "violet" },
  adjustment: { ar: "تعديل يدويّ", en: "Manual adjustment", tone: "slate" },
};

const STATE_LABELS: Record<string, { ar: string; en: string }> = {
  pending: { ar: "معلَّق", en: "Pending" },
  available: { ar: "متاح", en: "Available" },
  paid_out: { ar: "مصروف", en: "Paid out" },
};

function toneClasses(tone: string): string {
  switch (tone) {
    case "emerald":
      return "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400";
    case "rose":
      return "bg-rose-500/10 text-rose-700 dark:text-rose-400";
    case "sky":
      return "bg-sky-500/10 text-sky-700 dark:text-sky-400";
    case "amber":
      return "bg-amber-500/10 text-amber-700 dark:text-amber-400";
    case "violet":
      return "bg-violet-500/10 text-violet-700 dark:text-violet-400";
    default:
      return "bg-muted text-foreground";
  }
}

function OrderTransactionsList({ orderId, isAr }: { orderId: string; isAr: boolean }) {
  const fetchTxns = useServerFn(getOrderTransactions);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-order-txns", orderId],
    queryFn: () => fetchTxns({ data: { orderId } }),
    staleTime: 30_000,
  });

  const heading = isAr ? "الحركات الماليّة المرتبطة" : "Linked wallet transactions";

  if (isLoading) {
    return (
      <div>
        <div className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">
          {heading}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          {isAr ? "جارٍ التحميل…" : "Loading…"}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">
          {heading}
        </div>
        <div className="text-xs text-rose-600">{(error as Error).message}</div>
      </div>
    );
  }

  const txns: OrderTxn[] = data?.transactions ?? [];

  if (txns.length === 0) {
    return (
      <div>
        <div className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">
          {heading}
        </div>
        <div className="text-xs text-muted-foreground">
          {isAr
            ? "لا حركات ماليّة لهذا الطلب (لا يوجد سفير مرتبط)."
            : "No wallet transactions for this order (no linked ambassador)."}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {heading}
        </div>
        <div className="text-[10px] text-muted-foreground">
          {txns.length} {isAr ? "حركة" : "txns"}
        </div>
      </div>
      <div className="overflow-x-auto rounded-none border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/30 text-muted-foreground">
            <tr>
              <th className="px-2 py-1.5 text-start font-medium">{isAr ? "التاريخ" : "Date"}</th>
              <th className="px-2 py-1.5 text-start font-medium">{isAr ? "النوع" : "Type"}</th>
              <th className="px-2 py-1.5 text-end font-medium">{isAr ? "المبلغ" : "Amount"}</th>
              <th className="px-2 py-1.5 text-start font-medium">{isAr ? "الحالة" : "State"}</th>
              <th className="px-2 py-1.5 text-start font-medium">{isAr ? "السفير" : "Ambassador"}</th>
              <th className="px-2 py-1.5 text-start font-medium">{isAr ? "ملاحظة" : "Note"}</th>
            </tr>
          </thead>
          <tbody>
            {txns.map((t) => {
              const meta = TXN_TYPE_LABELS[t.txn_type] ?? {
                ar: t.txn_type,
                en: t.txn_type,
                tone: "slate",
              };
              const state = STATE_LABELS[t.balance_state] ?? {
                ar: t.balance_state,
                en: t.balance_state,
              };
              const isNegative = t.amount < 0;
              const dt = new Date(t.created_at);
              return (
                <tr key={t.id} className="border-t border-border">
                  <td className="whitespace-nowrap px-2 py-1.5 text-muted-foreground" title={dt.toISOString()}>
                    {dt.toLocaleString(isAr ? "ar" : "en", { dateStyle: "short", timeStyle: "short" })}
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[11px]", toneClasses(meta.tone))}>
                      {isAr ? meta.ar : meta.en}
                    </span>
                  </td>
                  <td className={cn("whitespace-nowrap px-2 py-1.5 text-end font-mono", isNegative ? "text-rose-600" : "text-emerald-700 dark:text-emerald-400")}>
                    {isNegative ? "" : "+"}{t.amount.toFixed(2)} {t.currency}
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">{isAr ? state.ar : state.en}</td>
                  <td className="px-2 py-1.5">
                    {t.ambassador_name ? (
                      <span className="text-foreground">
                        {t.ambassador_name}
                        {t.ambassador_code && (
                          <span className="ms-1 text-[10px] text-muted-foreground">({t.ambassador_code})</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-muted-foreground">{t.note ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
